// Executable-skill review drawer (S-EXEC P-E3, P2-5).
//
// The reviewer surface an owner/administrator uses to inspect an executable
// (scripts) skill before it may publish + run. Loads the static-check report
// (block/warn findings + the deterministic script hash), shows recent sandbox
// runs, and records an APPROVE/REJECT decision. Approval PINS the current bytes
// (a later edit makes the review stale). A blocking static finding hard-disables
// Approve — a reviewer cannot wave through a banned primitive. No alert(): every
// outcome surfaces inline. Mirrors SkillDetailDrawer's drawer→tabs→footer shape.

import { useState, useEffect, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import { MATERIALS } from "../../theme/materials";
import { skillsApi } from "../../api/client";

const RED = COLORS.red || "#ef4444";
const GREEN = COLORS.green || "#10b981";
const AMBER = "#d97706";
const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

function unwrap(res) {
  return res && res.data && typeof res.data === "object" ? res.data : res;
}

function sevColor(sev) {
  return sev === "block" ? RED : AMBER;
}
function sevBg(sev) {
  return sev === "block" ? `${RED}18` : `${AMBER}1c`;
}

function FindingsBody({ report, scripts, scriptHash }) {
  const findings = (report && report.findings) || [];
  const blocking = (report && report.blocking) || 0;
  const warnings = (report && report.warnings) || 0;
  const banner = !findings.length
    ? { c: GREEN, t: "No static-check findings — the script uses only allowlisted operations." }
    : blocking
      ? { c: RED, t: `${blocking} blocking finding(s) — publish and approval are refused until removed.` }
      : { c: AMBER, t: `${warnings} warning(s) for you to confirm — not blocking.` };

  return (
    <div>
      <div style={{
        border: `1px solid ${banner.c}55`, background: `${banner.c}14`, color: banner.c,
        borderRadius: 9, padding: "9px 12px", fontSize: 12.5, marginBottom: 12,
      }}>{banner.t}</div>

      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: DIM, marginBottom: 4 }}>
        Scripts
      </div>
      <div style={{ fontFamily: MONO, fontSize: 12, color: COLORS.text, marginBottom: 8 }}>
        {(scripts || []).join(", ") || "—"}
      </div>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: DIM, marginBottom: 4 }}>
        Pinned on approve
      </div>
      <div style={{ fontFamily: MONO, fontSize: 11, color: MUTED, wordBreak: "break-all", marginBottom: 14 }}>
        {scriptHash || "—"}
      </div>

      {findings.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {findings.map((f, i) => (
            <div key={i} style={{
              border: `1px solid ${sevColor(f.severity)}44`, background: sevBg(f.severity),
              borderRadius: 9, padding: "9px 11px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase",
                  color: sevColor(f.severity),
                }}>{f.severity}</span>
                <span style={{ fontFamily: MONO, fontSize: 11.5, color: COLORS.text }}>{f.code}</span>
                <span style={{ marginLeft: "auto", fontFamily: MONO, fontSize: 11, color: DIM }}>
                  {f.path}:{f.line}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: COLORS.text, marginTop: 4 }}>{f.message}</div>
            </div>
          ))}
        </div>
      )}

      <p style={{ fontSize: 11, color: DIM, marginTop: 14 }}>
        Static analysis is defence-in-depth, not the cage — the cage is gVisor + no-network + the
        read-only broker. It makes intent visible and hard-blocks break-out primitives.
      </p>
    </div>
  );
}

function RunsBody({ runs, skillName }) {
  const mine = (runs || []).filter((r) => !skillName || r.skill_name === skillName);
  if (!mine.length) {
    return <div style={{ fontSize: 12.5, color: DIM }}>No runs yet for this skill.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {mine.map((r) => {
        const ok = r.outcome === "completed";
        return (
          <div key={r.id || r.run_id} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
            border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 12,
          }}>
            <span style={{ fontWeight: 650, color: ok ? GREEN : RED }}>{r.outcome}</span>
            <span style={{ color: DIM }}>{r.broker_calls ?? 0} broker call(s)</span>
            <span style={{ color: DIM }}>{r.duration_ms != null ? `${r.duration_ms} ms` : ""}</span>
            <span style={{ marginLeft: "auto", fontFamily: MONO, color: DIM }}>{r.run_id}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ScriptReviewDrawer({ skill, onClose, onReviewed }) {
  const [tab, setTab] = useState("findings");
  const [check, setCheck] = useState(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null); // {ok, text}

  const name = skill && skill.name;

  const load = useCallback(async () => {
    if (!name) return;
    setLoading(true); setError(null);
    try {
      const [c, r] = await Promise.all([
        skillsApi.skillStaticCheck(name).then(unwrap),
        skillsApi.sandboxRuns().then(unwrap).catch(() => ({ runs: [] })),
      ]);
      setCheck(c);
      setRuns((r && r.runs) || []);
    } catch {
      setError("Couldn't load the review for this skill.");
    } finally {
      setLoading(false);
    }
  }, [name]);

  useEffect(() => { load(); }, [load]);

  const decide = useCallback(async (decision) => {
    if (submitting || !name) return;
    setSubmitting(true); setToast(null);
    try {
      const updated = unwrap(await skillsApi.skillReview(name, decision, notes || null));
      setToast({ ok: decision === "approved", text: decision === "approved"
        ? "Approved — the current script bytes are pinned." : "Rejected." });
      if (onReviewed) onReviewed(updated);
    } catch (e) {
      const msg = (e && e.message) || "Couldn't record the decision.";
      setToast({ ok: false, text: msg });
    } finally {
      setSubmitting(false);
    }
  }, [submitting, name, notes, onReviewed]);

  if (!skill) return null;
  const report = check && check.report;
  const canApprove = !!(report && report.ok) && !submitting;

  const TabBtn = ({ id, label }) => {
    const on = tab === id;
    return (
      <button type="button" onClick={() => setTab(id)}
        style={{
          background: "transparent", border: "none", cursor: "pointer",
          padding: "9px 4px", marginRight: 16, fontSize: 12.5,
          fontWeight: on ? 600 : 500, color: on ? COLORS.text : DIM,
          borderBottom: `2px solid ${on ? COLORS.accent : "transparent"}`,
        }}>{label}</button>
    );
  };

  return (
    <div role="dialog" aria-label={`Review ${name}`}
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: "min(520px, 100vw)",
        ...MATERIALS.thickGlass(), borderRadius: 0,
        borderLeft: `1px solid ${BORDER}`, boxShadow: MATERIALS.spatialShadow(),
        display: "flex", flexDirection: "column", zIndex: 50,
      }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: `1px solid ${BORDER}` }}>
        <span style={{ fontSize: 20 }}>🛡️</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>Review · {name}</div>
          <div style={{ fontSize: 11.5, color: DIM }}>Executable skill — {(skill.scripts || []).length} script(s)</div>
        </div>
        <button type="button" aria-label="Close" onClick={onClose}
          style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, color: DIM }}>✕</button>
      </div>

      <div style={{ display: "flex", padding: "0 16px", borderBottom: `1px solid ${BORDER}` }}>
        <TabBtn id="findings" label="Findings" />
        <TabBtn id="runs" label="Runs" />
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
        {loading ? (
          <div style={{ color: DIM, fontSize: 13 }}>Loading review…</div>
        ) : error ? (
          <div style={{ color: RED, fontSize: 13 }}>{error}</div>
        ) : tab === "findings" ? (
          <FindingsBody report={report} scripts={check && check.scripts} scriptHash={check && check.script_hash} />
        ) : (
          <RunsBody runs={runs} skillName={name} />
        )}
      </div>

      {/* Decision footer */}
      <div style={{ borderTop: `1px solid ${BORDER}`, padding: "12px 16px" }}>
        {toast && (
          <div style={{ fontSize: 12.5, marginBottom: 10, color: toast.ok ? GREEN : RED }}>{toast.text}</div>
        )}
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Review notes (optional)"
          style={{
            width: "100%", minHeight: 52, resize: "vertical", boxSizing: "border-box",
            border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 10px",
            fontSize: 12.5, fontFamily: "inherit", color: COLORS.text,
            background: COLORS.surface, marginBottom: 10,
          }} />
        {!canApprove && report && !report.ok && (
          <div style={{ fontSize: 11.5, color: RED, marginBottom: 8 }}>
            Approval blocked — {report.blocking} blocking finding(s) must be removed by the author first.
          </div>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" disabled={submitting} onClick={() => decide("rejected")}
            style={{
              border: `1px solid ${RED}66`, background: "transparent", color: RED,
              borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 550,
              cursor: submitting ? "not-allowed" : "pointer",
            }}>Reject</button>
          <button type="button" disabled={!canApprove} onClick={() => decide("approved")}
            title={canApprove ? "" : "Fix blocking findings before approving"}
            style={{
              border: `1px solid ${COLORS.accent}`, background: COLORS.accent, color: "#fff",
              borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 600,
              cursor: canApprove ? "pointer" : "not-allowed", opacity: canApprove ? 1 : 0.5,
            }}>Approve &amp; pin</button>
        </div>
      </div>
    </div>
  );
}
